import { type ReactNode } from 'react'

import { IconButton, Pagination, Space, StatusBadge } from '@procurify/ui'
import styled from 'styled-components'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TermOrMethod } from '@webapp/modules/purchasing/features/list-terms-and-methods/models'
import messages from './messages'

// Full-width override so the inline-edit cells fill the DataTable cell
// (ui-memory "Full-Width Components in DataTable Cells")
const FullWidthCellDecoratorStyled = styled.div`
  width: 100%;

  > div {
    display: block;
    width: 100% !important;
  }
`

export interface ITermsMethodsTableProps {
  items: TermOrMethod[]
  /** Row flagged with the CREATED indicator (BHV-029) — FS-held state */
  justCreatedId?: number | null
  /** Row flagged with the UPDATED indicator (BHV-030) — FS-held state */
  justUpdatedId?: number | null
  isLoading: boolean
  page: number
  numPages: number
  onPrevPage: () => void
  onNextPage: () => void
  onDeleteItem?: (item: TermOrMethod) => void
  renderEditableCells?: (item: TermOrMethod) => {
    name: ReactNode
    description: ReactNode
  }
}

export const TermsMethodsTable = ({
  items,
  justCreatedId,
  justUpdatedId,
  isLoading,
  page,
  numPages,
  onPrevPage,
  onNextPage,
  onDeleteItem,
  renderEditableCells,
}: ITermsMethodsTableProps) => {
  const intl = useProcurifyIntl()

  const columns: DatatableColumnConfig<'BASIC', TermOrMethod>[] = [
    col('BASIC', {
      // Sentence-cased header — fixes the Angular lowercase "{type} Name"
      // quirk (BHV-027)
      label: intl.formatMessage(messages.columnName),
      getter: (item) =>
        renderEditableCells ? renderEditableCells(item).name : item.name,
      decorate: (cell) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnDescription),
      getter: (item) =>
        renderEditableCells
          ? renderEditableCells(item).description
          : item.description,
      decorate: (cell) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: '',
      getter: (item) => (
        <Space direction='horizontal' size='small' align='center'>
          {item.id === justCreatedId && (
            <StatusBadge
              type='success'
              text={intl.formatMessage(messages.createdIndicator)}
              size='small'
            />
          )}
          {item.id === justUpdatedId && (
            <StatusBadge
              type='warning'
              text={intl.formatMessage(messages.updatedIndicator)}
              size='small'
            />
          )}
          <IconButton
            icon='delete'
            aria-label={intl.formatMessage(messages.deleteItemButtonLabel, {
              name: item.name,
            })}
            htmlTitle={intl.formatMessage(messages.deleteItemButtonTitle)}
            onClick={() => onDeleteItem?.(item)}
          />
        </Space>
      ),
    }),
  ]

  const handlePageChange = (newPage: number) => {
    if (newPage > page) {
      onNextPage()
    } else if (newPage < page) {
      onPrevPage()
    }
  }

  return (
    <Space>
      <DataTable<TermOrMethod>
        id='terms-methods-table'
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        data={items}
        isLoading={isLoading}
        columns={columns}
        customizableColumns={false}
        persistCustomizedColumns={false}
        emptyStateText={intl.formatMessage(messages.emptyStateText)}
        emptyStateSize='small'
      />
      <Space direction='horizontal' justify='end'>
        <Pagination
          currentPage={page}
          numPages={numPages}
          onPageChange={handlePageChange}
        />
      </Space>
    </Space>
  )
}

TermsMethodsTable.displayName = 'TermsMethodsTable'
